import { useState, Fragment } from 'react';

import { Box } from '@gympass/yoga';
import { ChevronUp, ChevronDown, ChevronLeft } from '@gympass/yoga-icons';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useOnInit, useRequest } from 'hooks';
import Pin from 'public/assets/icons/pincut.svg';
import { getProgramTopics, LibraryProgram } from 'services';

import { Loading } from 'components';

import * as S from './styles';

const Program: React.FC = () => {
  const [programTopics, setProgramTopics] = useState<LibraryProgram>();
  const [clicked, setClicked] = useState<number | null>();

  const request = useRequest();

  const { programId } = useParams<{
    programId: string;
  }>();

  const getProgramTopicsRequest = async () => {
    request.setLoading();
    try {
      const response = await getProgramTopics(Number(programId));
      setProgramTopics(response);
      request.setSuccess();
    } catch (e) {
      request.setError(e);
    }
  };

  const toggle = async (topicId: number) => {
    if (clicked === topicId) {
      return setClicked(null);
    }
    return setClicked(topicId);
  };

  useOnInit(() => {
    getProgramTopicsRequest();
  });

  return (
    <>
      <Loading isLoading={request.isLoading()} />
      <S.ProgramContainer data-testid="programSection">
        <S.ProgramHeader>
          <S.ProgramHeaderContent>
            <S.BackButton>
              <S.BackLink to="/content-library" data-testid="programBackButton">
                <ChevronLeft stroke="#ffffff" width={24} height={24} />
                <FormattedMessage id="CONTENT.PROGRAM.BACKBUTTON" />
              </S.BackLink>
            </S.BackButton>
            <S.ProgramName data-testid="programTitle">
              {programTopics?.titleKey}
            </S.ProgramName>
            <S.ProgramDescription data-testid="programDescription">
              {programTopics?.description}
            </S.ProgramDescription>
            <S.ModulesCount data-testid="programModulesCount">
              {programTopics?.topics.length}{' '}
              {programTopics?.topics?.length &&
              programTopics?.topics.length > 1 ? (
                <FormattedMessage id="CONTENT.PROGRAM.MODULECOUNT.MULTIPLE" />
              ) : (
                <FormattedMessage id="CONTENT.PROGRAM.MODULECOUNT" />
              )}
            </S.ModulesCount>
          </S.ProgramHeaderContent>
          <S.PinIconsContainer>
            <S.PinIcon>
              <Pin />
            </S.PinIcon>
            <S.PinIcon>
              <Pin />
            </S.PinIcon>
          </S.PinIconsContainer>
        </S.ProgramHeader>
        <S.ActivitiesContent>
          {programTopics?.topics.map(topic => (
            <Fragment key={topic.id}>
              <S.ActivitiesTopics data-testid="programTopic">
                <Box>
                  <S.TopicsTitle>{topic.titleKey}</S.TopicsTitle>
                  <S.PagesCount data-testid="programTopicPagesCount">
                    {topic.pages.length}{' '}
                    {topic.pages.length && topic.pages.length > 1 ? (
                      <FormattedMessage id="CONTENT.PROGRAM.PAGECOUNT.MULTIPLE" />
                    ) : (
                      <FormattedMessage id="CONTENT.PROGRAM.PAGECOUNT" />
                    )}
                  </S.PagesCount>
                </Box>
                <S.TopicsButton
                  onClick={() => toggle(topic.id)}
                  small
                  data-testid="programTopicToggle"
                >
                  {clicked !== topic.id ? <ChevronDown /> : <ChevronUp />}
                </S.TopicsButton>
                <S.Dropdown isCollapsed={clicked === topic.id}>
                  {topic.pages.map((page, idx) => (
                    <S.TopicItem
                      data-testid="programTopicPage"
                      to={`/content-library/programs/${programId}/pages/${page.id}`}
                      state={{
                        programName: programTopics.titleKey,
                      }}
                      key={page.id}
                    >
                      {idx + 1}. {page.title}
                    </S.TopicItem>
                  ))}
                </S.Dropdown>
              </S.ActivitiesTopics>
            </Fragment>
          ))}
        </S.ActivitiesContent>
      </S.ProgramContainer>
    </>
  );
};

export default Program;
